// src/components/assistant/CodeBlock.tsx
//
// 🧩 Bloque de código de una respuesta del agente (lo usa SOLO MessageContent):
// etiqueta de lenguaje, Copiar («Copiado»), ajustar líneas, contraer/expandir si es
// largo (JSON/SQL antes que el resto), ```diff con líneas verdes/rojas y ```mermaid
// → bloque visual `diagram` de @orion/visuals (perezoso; si no valida, el código).
//
// El resaltado (highlight.js) se descarga la PRIMERA vez que aparece código y solo
// cuando la respuesta ya terminó (en vivo se pinta texto plano: no se re-resalta por
// frame). Su HTML viene de texto escapado por highlight.js; es el único innerHTML.

import { useEffect, useId, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Copy, WrapText } from 'lucide-react';
import { VisualBloque } from '@/components/visuals/VisualBloque';
import { useCopy } from './clipboard';

type HighlightModule = typeof import('./highlight');
let hlModule: HighlightModule | null = null;
let hlPromise: Promise<HighlightModule> | null = null;
function loadHighlighter(): Promise<HighlightModule> {
  if (!hlPromise) {
    hlPromise = import('./highlight').then((m) => (hlModule = m));
    hlPromise.catch(() => { hlPromise = null; });
  }
  return hlPromise;
}

// Alias → lenguaje registrado en highlight.ts (+ nombre visible).
const LANGS: Record<string, [string, string]> = {
  ts: ['typescript', 'TypeScript'], tsx: ['typescript', 'TSX'], typescript: ['typescript', 'TypeScript'],
  js: ['javascript', 'JavaScript'], jsx: ['javascript', 'JSX'], javascript: ['javascript', 'JavaScript'], mjs: ['javascript', 'JavaScript'], cjs: ['javascript', 'JavaScript'], node: ['javascript', 'JavaScript'],
  json: ['json', 'JSON'], jsonc: ['json', 'JSON'], json5: ['json', 'JSON'],
  sql: ['sql', 'SQL'], postgres: ['sql', 'SQL'], postgresql: ['sql', 'SQL'], psql: ['sql', 'SQL'], mysql: ['sql', 'SQL'], sqlite: ['sql', 'SQL'],
  py: ['python', 'Python'], python: ['python', 'Python'],
  sh: ['bash', 'Shell'], bash: ['bash', 'Bash'], shell: ['bash', 'Shell'], zsh: ['bash', 'Shell'], console: ['bash', 'Consola'],
  css: ['css', 'CSS'], html: ['xml', 'HTML'], xml: ['xml', 'XML'], svg: ['xml', 'SVG'],
  yml: ['yaml', 'YAML'], yaml: ['yaml', 'YAML'],
  diff: ['', 'Diff'], patch: ['', 'Diff'], mermaid: ['', 'Mermaid'], text: ['', 'Texto'], txt: ['', 'Texto'], plaintext: ['', 'Texto'],
};

/** Líneas visibles antes de ofrecer «Mostrar todo». JSON/SQL se contraen antes. */
const COLLAPSE_AT = 20;
const COLLAPSE_AT_DATA = 12;
const LINE_EM = 1.6;

export interface CodeBlockProps {
  code: string;
  /** Lenguaje del fence (```ts → "ts"). */
  lang?: string;
  /** La respuesta se está escribiendo: sin resaltado ni diagrama hasta que termine. */
  live?: boolean;
  /** ```mermaid como diagrama (false = mostrar el código, p. ej. en el respaldo). */
  diagrams?: boolean;
  /** Aviso bajo el encabezado (p. ej. «No se pudo dibujar el diagrama»). */
  note?: string;
}

export function CodeBlock({ code, lang = '', live = false, diagrams = true, note }: CodeBlockProps) {
  const key = lang.toLowerCase();
  const [hlLang, label] = LANGS[key] || [key, key ? key.toUpperCase() : 'Código'];

  // 📈 ```mermaid → el `diagram` de @orion/visuals (valida en el borde; si no, código).
  if (key === 'mermaid' && diagrams && !live && code.trim()) {
    const fallback = () => <CodeBlock code={code} lang="mermaid" diagrams={false} note="No se pudo dibujar el diagrama; se muestra el código." />;
    return (
      <div className="my-2">
        <VisualBloque
          bloque={{ tipo: 'visual', contenido: code, visual: { v: 1, kind: 'diagram', data: { syntax: 'mermaid', code } } }}
          renderFallback={fallback}
        />
      </div>
    );
  }
  return <CodeView code={code} hlLang={hlLang} label={label} kind={key === 'diff' || key === 'patch' ? 'diff' : 'code'} live={live} note={note} />;
}

function CodeView({ code, hlLang, label, kind, live, note }: { code: string; hlLang: string; label: string; kind: 'diff' | 'code'; live: boolean; note?: string }) {
  const id = useId();
  const [copied, copy] = useCopy();
  const [wrap, setWrap] = useState(false);
  const lines = useMemo(() => code.split('\n'), [code]);
  const limit = hlLang === 'json' || hlLang === 'sql' ? COLLAPSE_AT_DATA : COLLAPSE_AT;
  const long = lines.length > limit + 2; // +2: no se contrae por un par de líneas
  const [expanded, setExpanded] = useState(false);
  const collapsed = long && !expanded;

  const wantHl = kind === 'code' && !live && !!hlLang;
  const [hlReady, setHlReady] = useState(!!hlModule);
  useEffect(() => {
    if (!wantHl) return;
    // Pudo cargarse entre el render y este efecto (otro bloque lo pidió): solo marcar.
    if (hlModule) {
      setHlReady(true);
      return;
    }
    let alive = true;
    loadHighlighter().then(() => alive && setHlReady(true)).catch(() => undefined);
    return () => { alive = false; };
  }, [wantHl]);
  const html = useMemo(
    () => (wantHl && hlReady && hlModule ? hlModule.highlight(code, hlLang) : null),
    [wantHl, hlReady, code, hlLang],
  );

  const btn =
    'inline-flex h-7 items-center gap-1 rounded-lg px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-background/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className={`o-code ${kind === 'diff' ? 'o-code-diff' : ''} my-2 min-w-0 overflow-hidden rounded-xl border border-border bg-muted text-foreground`}>
      <div className="flex items-center gap-1 border-b border-border/80 py-1 pl-3 pr-1">
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
        <button type="button" className={`${btn} ${wrap ? 'text-primary' : ''}`} onClick={() => setWrap((w) => !w)} aria-pressed={wrap} title={wrap ? 'No ajustar líneas' : 'Ajustar líneas'}>
          <WrapText size={14} aria-hidden />
          <span className="sr-only">Ajustar líneas</span>
        </button>
        <button type="button" className={btn} onClick={() => copy(code)} title="Copiar código">
          {copied ? <Check size={14} className="text-primary" aria-hidden /> : <Copy size={14} aria-hidden />}
          <span aria-live="polite">{copied ? 'Copiado' : 'Copiar'}</span>
        </button>
      </div>
      {note && <p className="border-b border-border/80 px-3 py-1.5 text-xs text-muted-foreground">{note}</p>}
      <div className="relative">
        <pre
          id={id}
          className={`o-hl m-0 overflow-x-auto py-2.5 text-[12.5px] ${kind === 'diff' ? '' : 'px-3'} ${wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'}`}
          style={{ lineHeight: LINE_EM, ...(collapsed ? { maxHeight: `calc(${limit * LINE_EM}em + 0.625rem)`, overflowY: 'hidden' } : {}) }}
          tabIndex={0}
          aria-label={`Código ${label}`}
        >
          {kind === 'diff' ? (
            <code>
              {lines.map((ln, i) => {
                const cls = /^(\+\+\+|---)(\s|$)/.test(ln)
                  ? 'o-diff-meta'
                  : ln.startsWith('@@')
                    ? 'o-diff-hunk'
                    : ln.startsWith('+')
                      ? 'o-diff-add'
                      : ln.startsWith('-')
                        ? 'o-diff-del'
                        : '';
                const signed = cls === 'o-diff-add' || cls === 'o-diff-del';
                return (
                  <span key={i} className={`o-diff-line ${cls}`}>
                    {signed ? (
                      <>
                        <span className="o-diff-sign">{ln[0]}</span>
                        {ln.slice(1) || ' '}
                      </>
                    ) : (
                      ln || ' '
                    )}
                  </span>
                );
              })}
            </code>
          ) : html != null ? (
            <code dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <code>{code}</code>
          )}
        </pre>
        {collapsed && <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-muted to-transparent" />}
      </div>
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls={id}
          className="flex w-full items-center justify-center gap-1 border-t border-border/80 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {expanded ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
          {expanded ? 'Contraer' : `Mostrar todo (${lines.length} líneas)`}
        </button>
      )}
    </div>
  );
}
